import directionStraightRight from '@iconify/icons-carbon/direction-straight-right';
import emailIcon from '@iconify/icons-carbon/email';
import locationIcon from '@iconify/icons-carbon/location';
import phoneVoice from '@iconify/icons-carbon/phone-voice';
import clock from '@iconify/icons-material-symbols/nest-clock-farsight-analog-outline-rounded';
import { Divider, Grid, List, ListItem, ListItemIcon, Typography } from '@mui/material';
import { Trans, useTranslation } from 'next-i18next';
import { Fragment } from 'react';

import { TextIconLabel } from '@larvitu/shared/mui/ui-components';
import { Link } from '@larvitu/shared/next/mui';
import { IconifyIcon } from '@larvitu/shared/react/components/ui-iconify-icon';

import { GoogleMapIframe } from '../GoogleMapIframe';

import type { ArrivalProps } from './ContactUs';
import type { FC } from 'react';

interface Address {
  tel: string;
  fax: string;
  email: string;
  street: {
    name: string;
    number: string;
  };
  zipcode: string;
  city: string;
}

interface ContactInfoProps {
  address: Address;
  footer: ArrivalProps;
  openingHours: Array<{ day: string; time: string }>;
}

const defaultAddress = {
  tel: '',
  fax: '',
  email: '',
  street: {
    name: '',
    number: '',
  },
  zipcode: '',
  city: '',
};

const email = process.env.NEXT_PUBLIC_NODEMAILER_RECEIVERS

export const ContactInfo: FC<ContactInfoProps> = ({
  openingHours,
  footer,
  address = defaultAddress,
}) => {
  const { t } = useTranslation( 'common' );

  return (
    <Grid container spacing={3} justifyContent={{ md: 'space-between' }}>
      <Grid item xs={12} md={5} lg={6} justifyContent="center">
        <Grid container spacing={3} alignItems={{ md: 'flex-start' }}>
          <Grid item xs={12} sm={6} md={12}>
            <TextIconLabel
              icon={
                <IconifyIcon
                  icon={clock}
                  width={24}
                  height={24}
                  style={{
                    marginRight: '8px',
                  }}
                />
              }
              value={t( 'opening-hours', { ns: 'common' } )}
              sx={{ typography: 'subtitle2' }}
            />

            {openingHours.map((opening, index, array) => (
              <Fragment key={index}>
                <Grid container color="text.secondary" columnSpacing={3}>
                  <Grid item xs={6}>
                    <Typography component="span" variant="subtitle2">
                      {opening.day}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography component="span" variant="subtitle2">
                      <Trans components={{ wbr: <wbr /> }} t={t}>
                        {opening.time}
                      </Trans>
                    </Typography>
                  </Grid>
                </Grid>
                {array.length - 1 > index && <Divider />}
              </Fragment>
            ))}
          </Grid>

          <Grid item xs={6} /* md={12} */>
            <TextIconLabel
              icon={
                <IconifyIcon
                  icon={locationIcon}
                  width={24}
                  height={24}
                  style={{
                    marginRight: '8px',
                  }}
                />
              }
              value={t( 'address', { ns: 'common' } )}
              sx={{ typography: 'subtitle2' }}
            />
            <Typography color="text.secondary" variant="subtitle2">
              <Link
                href="https://goo.gl/maps/EDFtpMVQEVwLUpdn7"
                color="inherit"
              >
                {address.street.name} {address.street.number} <br />
                {address.zipcode} {address.city}
              </Link>
            </Typography>
          </Grid>

          <Grid item xs={6} /* md={12} */>
            <TextIconLabel
              icon={
                <IconifyIcon
                  icon={phoneVoice}
                  width={24}
                  height={24}
                  style={{
                    marginRight: '8px',
                  }}
                />
              }
              value={t( 'phone', { ns: 'common' } )}
              sx={{ typography: 'subtitle2' }}
            />

            <Typography variant="subtitle2">
              <Link
                href={`tel:${address.tel}`}
                color="text.secondary"
                underline="hover"
              >
                {address.tel}
              </Link>
            </Typography>
          </Grid>

          <Grid item xs={6} /* md={12} */>
            <TextIconLabel
              icon={
                <IconifyIcon
                  icon="mdi:fax"
                  width={24}
                  height={24}
                  style={{
                    marginRight: '8px',
                  }}
                />
              }
              value={t( 'fax', { ns: 'common' } )}
              sx={{ typography: 'subtitle2' }}
            />

            <Typography color="text.secondary" variant="subtitle2">
              {address.fax}
            </Typography>
          </Grid>

          <Grid item xs={6} /* md={12} */>
            <TextIconLabel
              icon={
                <IconifyIcon
                  icon={emailIcon}
                  width={24}
                  height={24}
                  style={{
                    marginRight: '8px',
                  }}
                />
              }
              value={t( 'e-mail', { ns: 'common' } )}
              sx={{ typography: 'subtitle2' }}
            />
            <Typography variant="subtitle2">
              <Link
                href={`mailto:${email}`}
                color="text.secondary"
                underline="hover"
              >
                {email?.split( ',' )[0].trim()}
              </Link>
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} md={7} lg={6}>
        <GoogleMapIframe
          marginY={{
            xs: 4,
            md: 0,
          }}
          src={'https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d2428.810459883262!2d13.330012!3d52.5006705!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47a850f90f8aa80b%3A0x8e57dbdc467de4fb!2sDr.%20med.%20G%C3%B6tz%20Geiges!5e0!3m2!1sde!2sde!4v1679565100614!5m2!1sde!2sde'}
        />
      </Grid>

      <Grid
        item
        xs={12}
        my={{
          xs: 4,
          md: 0,
        }}
      >
        <Divider
          sx={{
            borderStyle: 'dashed',
            width: 1,
          }}
        />

        <Typography my={3} paragraph color="text.secondary">
          {footer.header}
        </Typography>

        <Grid container>
          <Grid item xs={12} sm={6}>
            <Typography
              component="h3"
              variant="h5"
              fontWeight="bold"
              color="text.secondary"
            >
              {footer.publicTransportation.header}
            </Typography>
            <List>
              {footer.publicTransportation.stops.map((stop, index) => (
                <ListItem key={index} sx={{ paddingLeft: 0 }}>
                  <ListItemIcon>
                    <IconifyIcon icon={directionStraightRight} />
                  </ListItemIcon>
                  <Typography color="text.secondary">
                    {stop}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography
              component="h3"
              variant="h5"
              fontWeight="bold"
              color="text.secondary"
            >
              {footer.car.header}
            </Typography>
            <List>
              {footer.car.parkingLots.map((parkingLot, index) => (
                <ListItem key={index} sx={{ paddingLeft: 0 }}>
                  <ListItemIcon>
                    <IconifyIcon icon={directionStraightRight} />
                  </ListItemIcon>
                  <Typography color="text.secondary">
                    {parkingLot}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
